import React, { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';

import AppNavbar from '../components/AppNavbar';
import Header from '../components/Header';
import SideMenu from '../components/SideMenu';
import { useWorkspaceList } from '../components/WorkspacesTableView';
import WorkspaceCard from '../components/workspaces/WorkspaceCard';
import WorkspaceFiltersBar from '../components/workspaces/WorkspaceFiltersBar';
import WorkspaceListView from '../components/workspaces/WorkspaceListView';
import WorkspaceSummaryStrip from '../components/workspaces/WorkspaceSummaryStrip';
import {
  filterWorkspaceCards,
  mergeWorkspaceLists,
  normalizeWorkspaceCard,
  workspaceSummary,
} from '../components/workspaces/workspaceViewModel';
import AppTheme from '../shared-theme/AppTheme';
import {
  chartsCustomizations,
  dataGridCustomizations,
  datePickersCustomizations,
  treeViewCustomizations,
} from './theme/customizations';

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const emptyCopy = {
  active: {
    title: 'No active workspaces found',
    description: 'There are no current workspaces matching your search.',
  },
  attention: {
    title: 'Nothing needs attention',
    description: 'No workspace currently has unread or action-required activity.',
  },
  archived: {
    title: 'No archived workspaces found',
    description: 'Deactivated and archived workspaces will appear here.',
  },
};

export default function WorkspacesDashboard(props) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('active');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('cards');

  const requestorWorkspaces = useWorkspaceList({
    endpoint: '/requests/workspaces',
    errorMessage: 'Unable to load request workspaces right now.',
  });
  const providerWorkspaces = useWorkspaceList({
    endpoint: '/provider/workspaces',
    errorMessage: 'Unable to load provider workspaces right now.',
  });

  const workspaces = useMemo(() => {
    const combined = mergeWorkspaceLists(
      requestorWorkspaces.workspaces,
      providerWorkspaces.workspaces,
    );
    return combined
      .map(normalizeWorkspaceCard)
      .sort((left, right) => (
        new Date(right.updatedAt || 0).getTime() - new Date(left.updatedAt || 0).getTime()
      ));
  }, [providerWorkspaces.workspaces, requestorWorkspaces.workspaces]);

  const summary = useMemo(() => workspaceSummary(workspaces), [workspaces]);
  const archivedCount = useMemo(
    () => workspaces.filter((workspace) => workspace.isArchived).length,
    [workspaces],
  );
  const activeTabCount = workspaces.length - archivedCount;
  const visibleWorkspaces = useMemo(
    () => filterWorkspaceCards(workspaces, { tab: activeTab, search }),
    [activeTab, search, workspaces],
  );

  const loading = requestorWorkspaces.loading || providerWorkspaces.loading;
  const errors = [requestorWorkspaces.error, providerWorkspaces.error].filter(Boolean);
  const openWorkspace = (workspace) => navigate(`/workspaces/${workspace.id}`);

  return (
    <AppTheme {...props} themeComponents={xThemeComponents}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: 'flex' }}>
        <SideMenu />
        <AppNavbar />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minWidth: 0,
            p: { xs: 2, sm: 3 },
            mt: { xs: 7, md: 0 },
          }}
        >
          <Header
            breadcrumbItems={[
              { label: 'AMX xConsole', href: '/' },
              { label: 'Workspaces' },
            ]}
          />

          <Stack spacing={2.5} sx={{ maxWidth: 1500, mx: 'auto' }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'flex-end' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ fontWeight: 850 }}>
                  Workspaces
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                  Track collaborative request fulfillment, execution activity, artifacts, and provider workstreams.
                </Typography>
              </Box>
              <WorkspaceFiltersBar
                search={search}
                onSearchChange={setSearch}
                view={view}
                onViewChange={setView}
              />
            </Stack>

            <WorkspaceSummaryStrip summary={summary} loading={loading} />

            {errors.length ? (
              <Alert severity={errors.length === 2 ? 'error' : 'warning'}>
                {errors.length === 2
                  ? 'Workspaces could not be loaded. Please try again.'
                  : `${errors[0]} Showing the workspaces that are currently available.`}
              </Alert>
            ) : null}

            <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Tabs
                value={activeTab}
                onChange={(_event, nextTab) => setActiveTab(nextTab)}
                aria-label="Workspace status"
                variant="scrollable"
                scrollButtons="auto"
                sx={{ px: { xs: 1, sm: 2 } }}
              >
                <Tab value="active" label={`Active (${activeTabCount})`} />
                <Tab value="attention" label={`Needs Attention (${summary.attention})`} />
                <Tab value="archived" label={`Archived (${archivedCount})`} />
              </Tabs>
              <Divider />
              <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                {loading ? (
                  <Stack alignItems="center" spacing={1.5} sx={{ py: 7 }}>
                    <CircularProgress size={30} />
                    <Typography variant="body2" color="text.secondary">
                      Loading workspace activity...
                    </Typography>
                  </Stack>
                ) : null}

                {!loading && !visibleWorkspaces.length ? (
                  <Stack alignItems="center" spacing={0.5} sx={{ py: 7, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {emptyCopy[activeTab].title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {emptyCopy[activeTab].description}
                    </Typography>
                  </Stack>
                ) : null}

                {!loading && visibleWorkspaces.length && view === 'cards' ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'repeat(2, minmax(0, 1fr))',
                        xl: 'repeat(3, minmax(0, 1fr))',
                      },
                      gap: 2,
                    }}
                  >
                    {visibleWorkspaces.map((workspace) => (
                      <WorkspaceCard
                        key={workspace.id}
                        workspace={workspace}
                        onOpen={() => openWorkspace(workspace)}
                      />
                    ))}
                  </Box>
                ) : null}

                {!loading && visibleWorkspaces.length && view === 'list' ? (
                  <WorkspaceListView workspaces={visibleWorkspaces} onOpen={openWorkspace} />
                ) : null}
              </Box>
            </Card>
          </Stack>
        </Box>
      </Box>
    </AppTheme>
  );
}
